import { CountryCode } from 'types/generatedSchemaTypes';
import Logger from './Logger';

export type ServiceChip = {
  serviceChipId: string;
  text: { title: string; description: string };
  style?: { backgroundColor?: string };
  countriesActive?: CountryCode[];
  redirectUrl: string;
  imageSource?: string;
};

type FeedbackTriggerType = 'byCount' | 'byFrequency';

export interface FeedbackTriggerConfig {
  feedbackTriggerType: FeedbackTriggerType;
  values?: number[]; // Used when type is "byCount"
  frequency?: number; // Used when type is "byFrequency"
}

interface OpenHours {
  mondayToFriday: string;
  saturday: string;
  sunday: string;
}

interface CountryInfo {
  phoneNumber: string;
  callPrice: {
    perCall: string;
    perMin: string;
  } | null;
  emailAddress: string;
  openHours: OpenHours;
  reclamationUrl: string;
}

export type CountryRegionDynamicInfo = {
  [key in CountryCode]: CountryInfo;
};

interface ParkAndRideUrlInfo {
  termsUrl: string;
  privacyUrl: string;
  customerUrl: string;
}

export type Language = 'EN' | 'FI' | 'NO' | 'SV';

export type ParkAndRideDynamicInfo = {
  [key in Language]: ParkAndRideUrlInfo;
};

export type FeedbackCount = {
  [key in CountryCode]: number;
};

export interface SubscriptionLinkInfo {
  terms?: string;
  info?: string;
}

export type SubscriptionLinksDynamicInfo = {
  [key in CountryCode]?: SubscriptionLinkInfo;
};

// Return boolean value to show or hide a feature.
// If the value is not defined, the feature should be hidden (defaults to false).
// Used when the feature flag is defined as a boolean in Firebase Remote Config.
export const showFeatureFlag = (featureFlag?: string): boolean => {
  return featureFlag === undefined ? false : featureFlag === 'true';
};

// Return boolean value to show or hide a feature.
// Show the feature if the current value is included in the allowed values or
// if the allowed values is ALL. If undefined/empty, hide the feature.
// Used when the feature flag is defined as a comma separated string in Firebase Remote Config.
export const showFeatureFlagByValue = (
  allowedValues: string | undefined,
  currentValue: string
) => {
  if (!allowedValues) return false;
  return allowedValues === 'ALL'
    ? true
    : allowedValues.split(',').indexOf(currentValue) > -1;
};

// Filter list of items by a feature flag.
// Used when the feature flag is defined as a comma separated string in Firebase Remote Config.
export const filteredItemsByFeatureFlag = (
  items: any[],
  filterProperty: string,
  featureFlag?: string
): any[] => {
  if (featureFlag === undefined) return [];
  if (featureFlag === 'true') return items;
  if (!featureFlag) return [];
  return items.filter((item) => {
    return featureFlag.indexOf(item[filterProperty]) > -1;
  });
};

export type TypeDictionary<T extends string | symbol | number, U> = {
  [K in T]: U;
};

type FlagName =
  | 'service_chips'
  | 'sales_order_permits'
  | 'remote_tutorial'
  | 'country_region_dynamic_info'
  | 'chatbot_urls'
  | 'account_payment_ca_as_default_toggle_display'
  | 'feedback_trigger'
  | 'permit_start_date'
  | 'park_and_ride_content'
  | 'show_rfid_tag_registration_by_country'
  | 'enable_healthcare_permit_button'
  | 'enable_unlock_mg_automatic_pmc_button'
  | 'subscription_links'
  | 'intercom_enabled'
  | 'enable_mobilepay_by_country'
  | 'enable_adyen_card_add_by_country';

const getDefault = <T>(flagName: FlagName): T => {
  switch (flagName) {
    case 'sales_order_permits':
    case 'account_payment_ca_as_default_toggle_display':
      return { FI: true, SE: false } as T;
    case 'service_chips':
      return [] as T;
    case 'remote_tutorial':
      return [] as T;
    case 'country_region_dynamic_info':
      return { FI: {}, SE: {}, NO: {} } as T;
    case 'park_and_ride_content':
      return {
        FI: {
          termsUrl:
            'https://www.hsl.fi/hsl/kayttoehdot-uudet/hsln-joukkoliikenteen-yleiset-matkustus--ja-lippuehdot',
          privacyUrl: 'https://www.hsl.fi/hsl/tietosuoja',
          customerUrl: 'https://www.hsl.fi/asiakaspalvelu',
        },
        SV: {},
        EN: {
          termsUrl:
            'https://www.hsl.fi/en/hsl/new-terms-of-use/hsl-public-transport-conditions-of-carriage-and-ticket-terms-and-conditions',
          privacyUrl: 'https://www.hsl.fi/en/hsl/privacy-policy',
          customerUrl: 'https://www.hsl.fi/en/customer-service',
        },
      } as T;
    case 'feedback_trigger':
      return {
        feedbackTriggerType: 'byCount',
        values: [1, 3, 10],
        frequency: 15,
      } as T;
    case 'chatbot_urls':
      return {
        FI: {
          fi: 'https://www.aimopark.fi/asiakaspalvelu/ota-yhteytta/',
          en: 'https://www.aimopark.fi/en/customer-support/contact-us/',
          no: 'https://www.aimopark.fi/en/customer-support/contact-us/',
          sv: 'https://www.aimopark.fi/sv/kundtjanst/kontakta-oss/',
        },
      } as T;
    case 'permit_start_date':
      return { FI: false, SE: true } as T;
    case 'show_rfid_tag_registration_by_country':
      return { FI: false, SE: true } as T;
    case 'enable_healthcare_permit_button':
      return { FI: false, SE: false } as T;
    case 'enable_unlock_mg_automatic_pmc_button':
      return { FI: false, SE: false } as T;
    case 'subscription_links':
      return {
        FI: {
          terms:
            'https://www.aimopark.fi/asiakaspalvelu/terms-and-conditions-ltp/',
          info: 'https://www.aimopark.fi/',
        },
        SE: {
          terms: 'https://www.aimopark.se/kundservice/avtalsvillkor/',
          info: 'https://www.aimopark.se/',
        },
      } as T;
    case 'intercom_enabled':
      return { FI: false, SE: false } as T;
    case 'enable_mobilepay_by_country':
      return { FI: true, SE: false } as T;
    case 'enable_adyen_card_add_by_country':
      // Dark feature: default OFF in every country until explicitly enabled
      // via Remote Config (also the fallback when the flag is unparseable).
      return { FI: false, SE: false } as T;
  }
};

export const parseFeatureFlagObject = <T>(
  objectString: string,
  flagName: FlagName
) => {
  try {
    if (objectString) {
      const parsedObject: T = JSON.parse(objectString);
      return parsedObject;
    }
  } catch (e) {
    Logger.error(`Parsing ${flagName} | Value: ${objectString} | Error: ${e}`);
  }
  return getDefault<T>(flagName);
};

export const isIntercomEnabledForCountry = (
  intercomEnabledFlags: string,
  countryCode: CountryCode
): boolean => {
  const enabledByCountry = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(intercomEnabledFlags, 'intercom_enabled');
  return enabledByCountry?.[countryCode] === true;
};

export const isMobilePayEnabledForCountry = (
  enableMobilePayByCountryFlag: string | undefined,
  appCountryCode: CountryCode | null | undefined,
  customerEmail?: string | null
): boolean => {
  if (!enableMobilePayByCountryFlag || !appCountryCode) return false;
  const parsed = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean> & {
      WHITELIST_CUSTOMERS?: string[];
    }
  >(enableMobilePayByCountryFlag, 'enable_mobilepay_by_country');
  const countryEnabled = parsed?.[appCountryCode] === true;
  const whitelist = parsed?.WHITELIST_CUSTOMERS;
  if (!whitelist || whitelist.length === 0) {
    return countryEnabled;
  }
  return countryEnabled && !!customerEmail && whitelist.includes(customerEmail);
};

// Dark Adyen card-add flag. Mirrors isMobilePayEnabledForCountry exactly:
// same JSON parse + country gate AND, when a non-empty WHITELIST_CUSTOMERS
// list is present, customer-email membership. Defaults OFF when the flag is
// absent, empty, or unparseable.
export const isAdyenCardAddEnabledForCountry = (
  enableAdyenCardAddByCountryFlag: string | undefined,
  appCountryCode: CountryCode | null,
  customerEmail?: string | null
): boolean => {
  if (!enableAdyenCardAddByCountryFlag || !appCountryCode) return false;
  const parsed = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean> & {
      WHITELIST_CUSTOMERS?: string[];
    }
  >(enableAdyenCardAddByCountryFlag, 'enable_adyen_card_add_by_country');
  const countryEnabled = parsed?.[appCountryCode] === true;
  const whitelist = parsed?.WHITELIST_CUSTOMERS;
  if (!whitelist || whitelist.length === 0) {
    return countryEnabled;
  }
  return countryEnabled && !!customerEmail && whitelist.includes(customerEmail);
};
